import { useState, useEffect, useCallback } from "react";

// ══════════════════════════════════════════════════
//  SUPABASE CONFIG  (same as main app)
// ══════════════════════════════════════════════════
const SUPABASE_URL = "https://krswaklzkmmlarzcsylh.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_3kVUCXxxm6uEOFIq8KhZ-w_7cTcnIft";

async function supaFetch(path, options = {}) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      Prefer: options.prefer || "",
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── DB helpers ──────────────────────────────────────
async function dbGetAllWeddings() {
  return supaFetch("wedding?select=*&order=id.asc") || [];
}
async function dbCreateWedding(data) {
  return supaFetch("wedding", {
    method: "POST",
    prefer: "return=representation",
    body: JSON.stringify(data),
  });
}
async function dbUpdateWedding(id, fields) {
  return supaFetch(`wedding?id=eq.${id}`, {
    method: "PATCH",
    prefer: "return=representation",
    body: JSON.stringify(fields),
  });
}
async function dbGetGuestCount(weddingId) {
  const rows = await supaFetch(
      `guests?wedding_id=eq.${weddingId}&select=id,status`
  ).catch(() => []);
  return rows || [];
}
async function dbCheckUsernameUnique(username, excludeId = null) {
  let path = `wedding?username=eq.${encodeURIComponent(username)}&select=id&limit=1`;
  const rows = await supaFetch(path).catch(() => []);
  if (!rows || rows.length === 0) return true;
  if (excludeId !== null && rows[0].id === excludeId) return true;
  return false;
}

// ── Admin credentials (hardcoded, client-side only) ──
const ADMIN_USER = process.env.REACT_APP_ADMIN_USERNAME;
const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASSWORD;

function formatDate(d) {
  if (!d) return "—";
  const months = [
    "gen","feb","mar","apr","mag","giu",
    "lug","ago","set","ott","nov","dic",
  ];
  const [y, m, day] = d.split("-");
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
}

// ════════════════════════════════════════════════════
//  STYLES (injected once)
// ════════════════════════════════════════════════════
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&family=Jost:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:        #0f0d0c;
    --surface:   #181412;
    --card:      #1e1a18;
    --border:    #2e2420;
    --border2:   #3a2f28;
    --gold:      #c9a96e;
    --gold-dim:  #7a5f38;
    --rose:      #a06050;
    --rose-dim:  #5a3428;
    --green:     #5a8a60;
    --green-dim: #2a4a2e;
    --red:       #8a4040;
    --red-dim:   #4a2020;
    --text:      #e8ddd5;
    --text-mid:  #8a7a70;
    --text-dim:  #4a3f38;
    --mono:      'DM Mono', monospace;
    --serif:     'Cormorant Garamond', Georgia, serif;
    --sans:      'Jost', sans-serif;
  }

  body { background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; min-height: 100vh; }

  /* ── Layout ── */
  .app { display: flex; min-height: 100vh; }
  .sidebar { width: 220px; background: var(--surface); border-right: 1px solid var(--border); display: flex; flex-direction: column; flex-shrink: 0; }
  .main { flex: 1; overflow-y: auto; padding: 40px 48px; }

  /* ── Sidebar ── */
  .logo { padding: 28px 24px 24px; border-bottom: 1px solid var(--border); }
  .logo-title { font-family: var(--serif); font-style: italic; font-weight: 300; font-size: 22px; color: var(--gold); letter-spacing: 0.5px; }
  .logo-sub { font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: var(--text-mid); margin-top: 4px; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 11px 24px; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-mid); cursor: pointer; border: none; background: none; width: 100%; text-align: left; transition: color 0.15s, background 0.15s; border-left: 2px solid transparent; }
  .nav-item:hover { color: var(--text); background: rgba(201,169,110,0.04); }
  .nav-item.active { color: var(--gold); border-left-color: var(--gold); background: rgba(201,169,110,0.07); }
  .nav-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
  .sidebar-footer { margin-top: auto; padding: 20px 24px; border-top: 1px solid var(--border); }

  /* ── Page header ── */
  .page-title { font-family: var(--serif); font-style: italic; font-weight: 300; font-size: 34px; color: var(--text); letter-spacing: 0.3px; }
  .page-sub { font-size: 12px; color: var(--text-mid); margin-top: 6px; letter-spacing: 0.5px; }

  /* ── Cards ── */
  .card { background: var(--card); border: 1px solid var(--border); border-radius: 2px; padding: 28px 32px; }
  .card + .card { margin-top: 20px; }

  /* ── Form ── */
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .form-group { margin-bottom: 16px; }
  .form-group:last-child { margin-bottom: 0; }
  label { display: block; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--text-mid); margin-bottom: 7px; }
  input, select { width: 100%; background: var(--bg); border: 1px solid var(--border2); color: var(--text); font-family: var(--sans); font-size: 13px; padding: 9px 12px; border-radius: 2px; outline: none; transition: border-color 0.15s; }
  input:focus, select:focus { border-color: var(--gold-dim); }
  input::placeholder { color: var(--text-dim); }
  select option { background: var(--surface); }

  /* ── Buttons ── */
  .btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 20px; font-family: var(--sans); font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; border: 1px solid var(--border2); background: none; color: var(--text-mid); cursor: pointer; border-radius: 2px; transition: all 0.15s; white-space: nowrap; }
  .btn:hover { color: var(--text); border-color: var(--text-mid); }
  .btn-gold { background: var(--gold); border-color: var(--gold); color: #1a1008; font-weight: 500; }
  .btn-gold:hover { background: #d4b478; border-color: #d4b478; color: #1a1008; }
  .btn-danger { border-color: var(--red); color: var(--rose); }
  .btn-danger:hover { background: var(--red-dim); color: #e88; border-color: #c06060; }
  .btn-ghost { border: none; padding: 6px 10px; font-size: 11px; }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Table ── */
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead th { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: var(--text-dim); padding: 0 16px 12px; text-align: left; border-bottom: 1px solid var(--border); font-weight: 400; }
  tbody tr { border-bottom: 1px solid var(--border); transition: background 0.1s; }
  tbody tr:hover { background: rgba(255,255,255,0.02); }
  tbody td { padding: 14px 16px; font-size: 13px; vertical-align: middle; }
  .mono { font-family: var(--mono); font-size: 12px; color: var(--text-mid); }

  /* ── Badges ── */
  .badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 2px; font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; font-weight: 500; }
  .badge-active { background: var(--green-dim); color: #80d090; border: 1px solid #3a6a40; }
  .badge-inactive { background: var(--red-dim); color: #cc7070; border: 1px solid #6a3030; }

  /* ── Stats bar ── */
  .stats-row { display: flex; gap: 16px; margin-bottom: 32px; }
  .stat-box { flex: 1; background: var(--card); border: 1px solid var(--border); padding: 20px 24px; }
  .stat-num { font-family: var(--serif); font-size: 38px; font-weight: 300; color: var(--gold); line-height: 1; }
  .stat-lbl { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--text-mid); margin-top: 6px; }

  /* ── Login ── */
  .login-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: var(--bg); }
  .login-box { width: 360px; }

  /* ── Modal overlay ── */
  .overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal { background: var(--card); border: 1px solid var(--border2); width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; }
  .modal-header { padding: 28px 32px 20px; border-bottom: 1px solid var(--border); }
  .modal-body { padding: 28px 32px; }
  .modal-footer { padding: 16px 32px 24px; display: flex; justify-content: flex-end; gap: 10px; }

  /* ── Toast ── */
  .toast { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%) translateY(12px); background: var(--card); border: 1px solid var(--border2); color: var(--text); padding: 11px 22px; font-size: 12px; letter-spacing: 0.5px; border-radius: 2px; opacity: 0; pointer-events: none; transition: opacity 0.2s, transform 0.2s; z-index: 999; white-space: nowrap; }
  .toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
  .toast.ok { border-left: 3px solid var(--green); }
  .toast.error { border-left: 3px solid var(--rose); }

  /* ── Divider ── */
  .divider { height: 1px; background: var(--border); margin: 24px 0; }
  .section-title { font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--text-dim); margin-bottom: 16px; }

  /* ── Empty state ── */
  .empty { text-align: center; padding: 60px 20px; color: var(--text-dim); }
  .empty-icon { font-size: 32px; opacity: 0.3; margin-bottom: 12px; }

  /* ── Pill toggle ── */
  .toggle { display: inline-flex; background: var(--bg); border: 1px solid var(--border); border-radius: 2px; overflow: hidden; }
  .toggle-btn { padding: 7px 16px; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; border: none; background: none; color: var(--text-dim); cursor: pointer; transition: all 0.15s; }
  .toggle-btn.active { background: var(--gold-dim); color: var(--gold); }

  @media (max-width: 700px) {
    .sidebar { display: none; }
    .main { padding: 24px 16px; }
    .form-row, .form-row-3 { grid-template-columns: 1fr; }
    .stats-row { flex-wrap: wrap; }
  }
`;

// ════════════════════════════════════════════════════
//  TOAST
// ════════════════════════════════════════════════════
function Toast({ msg, type }) {
  return <div className={`toast ${msg ? "show" : ""} ${type}`}>{msg}</div>;
}

function useToast() {
  const [t, setT] = useState({ msg: "", type: "ok" });
  const ref = { current: null };
  const show = useCallback((msg, type = "ok") => {
    setT({ msg, type });
    clearTimeout(ref.current);
    ref.current = setTimeout(() => setT({ msg: "", type: "ok" }), 3000);
  }, []);
  return [t, show];
}

// ════════════════════════════════════════════════════
//  LOGIN PAGE
// ════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");

  const submit = () => {
    if (user === ADMIN_USER && pass === ADMIN_PASS) {
      onLogin();
    } else {
      setErr("Identifiants incorrects.");
    }
  };

  return (
      <div className="login-wrap">
        <div className="login-box">
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 36, fontWeight: 300, color: "var(--gold)", letterSpacing: 0.5 }}>
              Promessa
            </div>
            <div style={{ fontSize: 9, letterSpacing: 4, textTransform: "uppercase", color: "var(--text-mid)", marginTop: 6 }}>
              Panneau Admin
            </div>
          </div>
          <div className="card">
            <div className="form-group">
              <label>Identifiant</label>
              <input value={user} onChange={e => setUser(e.target.value)} placeholder="admin" autoFocus />
            </div>
            <div className="form-group">
              <label>Mot de passe</label>
              <input type="text" value={pass} onChange={e => setPass(e.target.value)}
                     placeholder="••••••••" onKeyDown={e => e.key === "Enter" && submit()} />
            </div>
            {err && <div style={{ color: "#cc7070", fontSize: 12, marginBottom: 12 }}>{err}</div>}
            <button className="btn btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={submit}>
              Se connecter
            </button>
          </div>
        </div>
      </div>
  );
}

// ════════════════════════════════════════════════════
//  MODAL — Create wedding account
// ════════════════════════════════════════════════════
function CreateModal({ onClose, onCreated, showToast }) {
  const [form, setForm] = useState({
    name1: "", name2: "", username: "", password: "",
    date: "", venue: "", active: true,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    // Admin can only create credentials
    // if (!form.name1.trim() || !form.name2.trim()) { showToast("Prénom 1 et 2 requis.", "error"); return; }
    if (!form.username.trim() || !form.password.trim()) { showToast("Identifiant et mot de passe requis.", "error"); return; }

    const payload = {
      name1: form.name1.trim(),
      name2: form.name2.trim(),
      username: form.username.trim(),
      password: form.password.trim(),
      date: form.date || null,
      venue: form.venue.trim() || null,
      active: form.active,
    };

    setLoading(true);
    try {
      const isUnique = await dbCheckUsernameUnique(form.username.trim());
      if (!isUnique) {
        showToast(`L'identifiant "${form.username.trim()}" est déjà utilisé.`, "error");
        setLoading(false);
        return;
      }
      await dbCreateWedding(payload);
      showToast(`Compte créé · ${form.name1} & ${form.name2}`);
      onCreated();
    } catch (e) {
      showToast(e.message || "Erreur lors de la création.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-header">
            <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 24, fontWeight: 300, color: "var(--gold)" }}>
              Créer un compte mariage
            </div>
            <div style={{ fontSize: 11, color: "var(--text-mid)", marginTop: 4 }}>
              Les identifiants seront transmis aux époux
            </div>
          </div>
          <div className="modal-body">
            <div className="section-title">Les époux</div>
            <div className="form-row">
              <div className="form-group">
                <label>Prénom 1</label>
                <input placeholder="ex: Sophie" value={form.name1} onChange={e => set("name1", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Prénom 2</label>
                <input placeholder="ex: Julien" value={form.name2} onChange={e => set("name2", e.target.value)} />
              </div>
            </div>
            {form.name1 && form.name2 && (
                <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--mono)", marginTop: -8, marginBottom: 16 }}>

                </div>
            )}
            <div className="divider" />
            <div className="section-title">Accès</div>
            <div className="form-row">
              <div className="form-group">
                <label>Identifiant</label>
                <input placeholder="ex: sophie-julien" value={form.username} onChange={e => set("username", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input placeholder="ex: mariage2025" value={form.password} onChange={e => set("password", e.target.value)} />
              </div>
            </div>
            <div className="divider" />
            <div className="section-title">Détails (optionnel)</div>
            <div className="form-row">
              <div className="form-group">
                <label>Date du mariage</label>
                <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Lieu</label>
                <input placeholder="ex: Château de la Lumière" value={form.venue} onChange={e => set("venue", e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 4 }}>
              <label>Statut initial</label>
              <div className="toggle">
                <button className={`toggle-btn ${form.active ? "active" : ""}`} onClick={() => set("active", true)}>✓ Actif</button>
                <button className={`toggle-btn ${!form.active ? "active" : ""}`} onClick={() => set("active", false)}>✕ Inactif</button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={onClose}>Annuler</button>
            <button className="btn btn-gold" onClick={submit} disabled={loading}>
              {loading ? "Création…" : "Créer le compte"}
            </button>
          </div>
        </div>
      </div>
  );
}

// ════════════════════════════════════════════════════
//  VIEW MODAL — Wedding detail + credentials
// ════════════════════════════════════════════════════
function ViewModal({ wedding, guestStats, onClose, onEdit }) {
  const confirmed = guestStats.filter(g => g.status === "confirmed").length;
  const declined  = guestStats.filter(g => g.status === "declined").length;
  const pending   = guestStats.filter(g => g.status === "pending").length;
  const appUrl    = `${window.location.origin}/${wedding.id}`;

  const copy = (text) => navigator.clipboard.writeText(text).catch(() => {});

  return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 24, fontWeight: 300, color: "var(--gold)" }}>
                  {wedding.name1} & {wedding.name2}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-mid)", marginTop: 4, fontFamily: "var(--mono)" }}>
                  /#{wedding.id}
                </div>
              </div>
              <button className="btn" style={{ fontSize: 11, marginTop: 2, flexShrink: 0 }} onClick={onEdit}>
                ✎ Modifier
              </button>
            </div>
          </div>
          <div className="modal-body">
            <div className="section-title">Accès compte</div>
            <div className="form-row" style={{ marginBottom: 20 }}>
              <div>
                <label>Identifiant</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--gold)", background: "var(--bg)", padding: "8px 12px", flex: 1, border: "1px solid var(--border2)" }}>
                    {wedding.username}
                  </code>
                  <button className="btn btn-ghost" onClick={() => copy(wedding.username)} title="Copier">⎘</button>
                </div>
              </div>
              <div>
                <label>Mot de passe</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--gold)", background: "var(--bg)", padding: "8px 12px", flex: 1, border: "1px solid var(--border2)" }}>
                    {wedding.password}
                  </code>
                  <button className="btn btn-ghost" onClick={() => copy(wedding.password)} title="Copier">⎘</button>
                </div>
              </div>
            </div>

            <div className="section-title">Lien app</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text-mid)", background: "var(--bg)", padding: "8px 12px", flex: 1, border: "1px solid var(--border2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {appUrl}
              </code>
              <button className="btn btn-ghost" onClick={() => copy(appUrl)} title="Copier">⎘</button>
            </div>

            <div className="divider" />
            <div className="section-title">Statistiques invités</div>
            <div style={{ display: "flex", gap: 12 }}>
              {[
                { n: guestStats.length, l: "Total",      c: "var(--text)" },
                { n: confirmed,         l: "Confirmés",  c: "#80d090" },
                { n: declined,          l: "Déclinés",   c: "#cc7070" },
                { n: pending,           l: "En attente", c: "var(--gold)" },
              ].map((s, i) => (
                  <div key={i} style={{ flex: 1, background: "var(--bg)", border: "1px solid var(--border)", padding: "14px 16px", textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--serif)", fontSize: 28, fontWeight: 300, color: s.c }}>{s.n}</div>
                    <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text-dim)", marginTop: 4 }}>{s.l}</div>
                  </div>
              ))}
            </div>

            {(wedding.date || wedding.venue) && (
                <>
                  <div className="divider" />
                  <div style={{ fontSize: 12, color: "var(--text-mid)" }}>
                    {wedding.date && <><span style={{ color: "var(--text-dim)", marginRight: 6, letterSpacing: 1, textTransform: "uppercase", fontSize: 10 }}>Date ·</span>{formatDate(wedding.date)}</>}
                    {wedding.venue && <span style={{ marginLeft: 16 }}><span style={{ color: "var(--text-dim)", marginRight: 6, letterSpacing: 1, textTransform: "uppercase", fontSize: 10 }}>Lieu ·</span>{wedding.venue}</span>}
                  </div>
                </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
  );
}

// ════════════════════════════════════════════════════
//  EDIT MODAL — Modify credentials + details
// ════════════════════════════════════════════════════
function EditModal({ wedding, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    name1:    wedding.name1    || "",
    name2:    wedding.name2    || "",
    username: wedding.username || "",
    password: wedding.password || "",
    date:     wedding.date     || "",
    venue:    wedding.venue    || "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.username.trim() || !form.password.trim()) {
      showToast("Identifiant et mot de passe ne peuvent pas être vides.", "error");
      return;
    }
    // Admin can only create credentials
    // if (!form.name1.trim() || !form.name2.trim()) {
    //   showToast("Les prénoms ne peuvent pas être vides.", "error");
    //   return;
    // }

    const payload = {
      name1:    form.name1.trim(),
      name2:    form.name2.trim(),
      username: form.username.trim(),
      password: form.password.trim(),
      date:     form.date || null,
      venue:    form.venue.trim() || null,
    };

    setLoading(true);
    try {
      // Check uniqueness only if username has changed
      if (form.username.trim() !== wedding.username) {
        const isUnique = await dbCheckUsernameUnique(form.username.trim(), wedding.id);
        if (!isUnique) {
          showToast(`L'identifiant "${form.username.trim()}" est déjà utilisé.`, "error");
          setLoading(false);
          return;
        }
      }
      await dbUpdateWedding(wedding.id, payload);
      showToast(`Modifications sauvegardées · ${form.name1} & ${form.name2}`);
      onSaved({ ...wedding, ...payload });
    } catch (e) {
      showToast(e.message || "Erreur lors de la sauvegarde.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-header">
            <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 24, fontWeight: 300, color: "var(--gold)" }}>
              Modifier l'événement
            </div>
            <div style={{ fontSize: 11, color: "var(--text-mid)", marginTop: 4, fontFamily: "var(--mono)" }}>
              id #{wedding.id}
            </div>
          </div>
          <div className="modal-body">

            {/* Credentials section */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div className="section-title" style={{ margin: 0 }}>Accès compte</div>
              <div style={{ fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1 }}>⚠ Communiquer aux époux après modification</div>
            </div>
            <div style={{ background: "rgba(201,169,110,0.05)", border: "1px solid var(--gold-dim)", padding: "20px 20px 4px", marginBottom: 20 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Identifiant</label>
                  <input value={form.username} onChange={e => set("username", e.target.value)} placeholder="ex: sophie-julien" />
                </div>
                <div className="form-group">
                  <label>Mot de passe</label>
                  <input value={form.password} onChange={e => set("password", e.target.value)} placeholder="ex: mariage2025" />
                </div>
              </div>
            </div>

            <div className="divider" />

            {/* Names */}
            <div className="section-title">Les époux</div>
            <div className="form-row">
              <div className="form-group">
                <label>Prénom 1</label>
                <input value={form.name1} onChange={e => set("name1", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Prénom 2</label>
                <input value={form.name2} onChange={e => set("name2", e.target.value)} />
              </div>
            </div>

            <div className="divider" />

            {/* Event details */}
            <div className="section-title">Détails de l'événement</div>
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Lieu</label>
                <input placeholder="ex: Château de la Lumière" value={form.venue} onChange={e => set("venue", e.target.value)} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={onClose}>Annuler</button>
            <button className="btn btn-gold" onClick={submit} disabled={loading}>
              {loading ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>
  );
}

// ════════════════════════════════════════════════════
//  WEDDINGS PAGE
// ════════════════════════════════════════════════════
function WeddingsPage({ showToast }) {
  const [weddings, setWeddings] = useState([]);
  const [guestMap, setGuestMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewWedding, setViewWedding] = useState(null);
  const [editWedding, setEditWedding] = useState(null);
  const [filter, setFilter] = useState("all"); // all | active | inactive

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ws = await dbGetAllWeddings();
      setWeddings(ws || []);
      // Load guest stats for all weddings in parallel
      const entries = await Promise.all(
          (ws || []).map(async w => {
            const gs = await dbGetGuestCount(w.id);
            return [w.id, gs];
          })
      );
      setGuestMap(Object.fromEntries(entries));
    } catch (e) {
      showToast("Erreur de chargement.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (w) => {
    try {
      await dbUpdateWedding(w.id, { active: !w.active });
      setWeddings(prev => prev.map(x => x.id === w.id ? { ...x, active: !x.active } : x));
      showToast(`${w.name1} & ${w.name2} — ${!w.active ? "activé" : "désactivé"}`);
    } catch (e) {
      showToast("Erreur lors de la mise à jour.", "error");
    }
  };

  const displayed = weddings.filter(w =>
      filter === "all" ? true : filter === "active" ? w.active : !w.active
  );

  const active   = weddings.filter(w => w.active).length;
  const inactive = weddings.filter(w => !w.active).length;

  return (
      <>
        {/* Stats */}
        <div className="stats-row">
          {[
            { n: weddings.length, l: "Mariages total" },
            { n: active,          l: "Actifs" },
            { n: inactive,        l: "Inactifs" },
            { n: Object.values(guestMap).reduce((s, gs) => s + gs.length, 0), l: "Invités total" },
          ].map((s, i) => (
              <div key={i} className="stat-box">
                <div className="stat-num">{s.n}</div>
                <div className="stat-lbl">{s.l}</div>
              </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
          <div className="toggle">
            {[["all","Tous"],["active","Actifs"],["inactive","Inactifs"]].map(([v, l]) => (
                <button key={v} className={`toggle-btn ${filter === v ? "active" : ""}`} onClick={() => setFilter(v)}>{l}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" onClick={load}>↻ Actualiser</button>
            <button className="btn btn-gold" onClick={() => setShowCreate(true)}>+ Nouveau compte</button>
          </div>
        </div>

        {/* Table */}
        <div className="card" style={{ padding: 0 }}>
          {loading ? (
              <div style={{ padding: "48px 32px", textAlign: "center", color: "var(--text-dim)", fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 18 }}>
                Chargement…
              </div>
          ) : displayed.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">◈</div>
                <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 18, color: "var(--text-mid)" }}>Aucun mariage</div>
              </div>
          ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                  <tr>
                    <th>Époux</th>
                    <th>Identifiant</th>
                    <th>Date</th>
                    <th>Invités</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  {displayed.map(w => {
                    const gs = guestMap[w.id] || [];
                    const confirmed = gs.filter(g => g.status === "confirmed").length;
                    return (
                        <tr key={w.id}>
                          <td>
                            <div style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 16, color: "var(--text)" }}>
                              {w.name1} & {w.name2}
                            </div>
                            <div className="mono" style={{ marginTop: 2 }}>/#{w.id}</div>
                          </td>
                          <td className="mono">{w.username}</td>
                          <td style={{ color: "var(--text-mid)", fontSize: 12 }}>{formatDate(w.date)}</td>
                          <td>
                            <span style={{ color: "var(--text)", fontWeight: 500 }}>{gs.length}</span>
                            {confirmed > 0 && <span style={{ color: "#80d090", fontSize: 11, marginLeft: 6 }}>({confirmed} ✓)</span>}
                          </td>
                          <td>
                        <span className={`badge ${w.active ? "badge-active" : "badge-inactive"}`}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
                          {w.active ? "Actif" : "Inactif"}
                        </span>
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                              <button className="btn btn-ghost" style={{ fontSize: 12 }}
                                      onClick={() => setViewWedding(w)} title="Voir les détails">
                                ◈ Détails
                              </button>
                              <button className="btn btn-ghost" style={{ fontSize: 12 }}
                                      onClick={() => setEditWedding(w)} title="Modifier">
                                ✎ Modifier
                              </button>
                              <button
                                  className={`btn btn-ghost ${w.active ? "btn-danger" : ""}`}
                                  style={{ fontSize: 12 }}
                                  onClick={() => toggleActive(w)}
                                  title={w.active ? "Désactiver" : "Activer"}
                              >
                                {w.active ? "✕ Désactiver" : "✓ Activer"}
                              </button>
                            </div>
                          </td>
                        </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
          )}
        </div>

        {showCreate && (
            <CreateModal
                onClose={() => setShowCreate(false)}
                onCreated={() => { setShowCreate(false); load(); }}
                showToast={showToast}
            />
        )}

        {viewWedding && (
            <ViewModal
                wedding={viewWedding}
                guestStats={guestMap[viewWedding.id] || []}
                onClose={() => setViewWedding(null)}
                onEdit={() => { setEditWedding(viewWedding); setViewWedding(null); }}
            />
        )}

        {editWedding && (
            <EditModal
                wedding={editWedding}
                onClose={() => setEditWedding(null)}
                showToast={showToast}
                onSaved={(updated) => {
                  setWeddings(prev => prev.map(w => w.id === updated.id ? updated : w));
                  setEditWedding(null);
                }}
            />
        )}
      </>
  );
}

// ════════════════════════════════════════════════════
//  ROOT APP
// ════════════════════════════════════════════════════
export default function AdminApp() {
  const [loggedIn, setLoggedIn] = useState(
      () => sessionStorage.getItem("promessa_admin") === "1"
  );
  const [page, setPage] = useState("weddings");
  const [toast, showToast] = useToast();

  const doLogin = () => {
    sessionStorage.setItem("promessa_admin", "1");
    setLoggedIn(true);
  };
  const doLogout = () => {
    sessionStorage.removeItem("promessa_admin");
    setLoggedIn(false);
  };

  return (
      <>
        <style>{STYLES}</style>

        {!loggedIn ? (
            <LoginPage onLogin={doLogin} />
        ) : (
            <div className="app">
              {/* Sidebar */}
              <aside className="sidebar">
                <div className="logo">
                  <div className="logo-title">Promessa</div>
                  <div className="logo-sub">Admin</div>
                </div>
                <nav style={{ padding: "16px 0", flex: 1 }}>
                  <button
                      className={`nav-item ${page === "weddings" ? "active" : ""}`}
                      onClick={() => setPage("weddings")}
                  >
                    <span className="nav-dot" />
                    Mariages
                  </button>
                </nav>
                <div className="sidebar-footer">
                  <button className="btn" style={{ width: "100%", justifyContent: "center", fontSize: 10 }} onClick={doLogout}>
                    Déconnexion
                  </button>
                </div>
              </aside>

              {/* Main */}
              <main className="main">
                <div style={{ marginBottom: 36 }}>
                  <div className="page-title">
                    {page === "weddings" ? "Gestion des mariages" : "—"}
                  </div>
                  <div className="page-sub">
                    {page === "weddings"
                        ? "Créer des comptes · activer ou désactiver des événements"
                        : ""}
                  </div>
                </div>

                {page === "weddings" && <WeddingsPage showToast={showToast} />}
              </main>
            </div>
        )}

        <Toast msg={toast.msg} type={toast.type} />
      </>
  );
}